import type { AxisSelection } from '@atlas/types'
import { RuleSeverity } from '@atlas/types'
import { ApplicableRulesCard } from '@atlas/ui-domain'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ApplicableRule } from '@/domain/rules.types'
import { useApplicableRules } from '@/hooks/useExplore'
import { useRuleEditing } from '@/hooks/useRuleEditing'
import { toExploreRuleListItems } from './rule-list-adapter'

export interface BrandRulesTabProps {
	brandCode: string
	/** The market the page resolved, or `null` when it is unscoped. */
	marketCode: string | null
}

/** Blocking only, alphabetical so the list does not reshuffle between reads. */
const topBlocking = (rules: ApplicableRule[]): ApplicableRule[] =>
	rules.filter((r) => r.severity === RuleSeverity.BLOCKING).sort((a, b) => a.statement.localeCompare(b.statement))

/**
 * The rules covering this brand in this market — the shared applicable-rules card, narrowed to
 * blocking by the host: the brand page leads with what would stop a deliverable, not with the
 * full inventory, which the Rules browser answers.
 *
 * Its own query rather than part of the brand-page read: rules live in rule-service, which
 * already depends on classification-service, so composing them server-side would close a
 * dependency cycle (ADR-0010).
 */
export function BrandRulesTab({ brandCode, marketCode }: BrandRulesTabProps) {
	const navigate = useNavigate()

	// The brand's code is the context, narrowed by the market the SERVER resolved — an unknown
	// code degrades to unscoped there, and the rules must answer the same question the page
	// states it is scoped to.
	const context: AxisSelection = useMemo(
		() => ({ brand: [brandCode], ...(marketCode ? { geography: [marketCode] } : {}) }),
		[brandCode, marketCode],
	)
	const { data: rules = [], isLoading, isError } = useApplicableRules(context)
	const { canEdit, editRule, ruleHref, openRule } = useRuleEditing()

	const rulesHref = `/rules?${new URLSearchParams({
		brand: brandCode,
		...(marketCode ? { geography: marketCode } : {}),
	}).toString()}`

	return (
		<ApplicableRulesCard
			rules={toExploreRuleListItems(topBlocking(rules))}
			title="Top blocking rules"
			viewAllHref={rulesHref}
			onViewAll={() => void navigate(rulesHref)}
			isLoading={isLoading}
			isError={isError}
			emptyLabel="No blocking rules apply to this brand."
			canEdit={canEdit}
			onEditRule={editRule}
			ruleHref={ruleHref}
			onOpenRule={openRule}
			note={canEdit ? 'Editing a rule changes it everywhere it resolves, not only on this brand.' : undefined}
		/>
	)
}
